import { InjectionToken } from '@angular/core';
import { PersistenceConfig } from '../app/shared/storage/persistence.interface';

interface EnvironementFormConfiguration {
	client: {
		birthdateWarningRange: {
			min: number;
			max: number;
		};
	};
	contact: {
		dateWarningRange: {
			min: number;
			max: number;
		};
		updateCreatePeriodAsDays: number;
	};
	career: {
		startOfCareerRange: {
			min: number;
			max: number;
		};
		typeAndScopeOfTheWorkplaceLength: number;
		remarkLength: number;
		jobTitleLength: number
	},
	assignment: {
		minDateOfCommissionOffsetDays: number;
	},
	lek: {
		resultLength: number;
	};
}

interface PaginationConfig {
	offset: number;
	range: number;
}

interface EnvironmentFileConfig {
	maxFileSize: number;
	allowedMimeTypes: string[];
}

interface EnvironmentContactSettings {
	maxFiles: number;
}

interface EnvironmentProofSettings {
	maxFiles: number;
	maxExpiryYears: number;
	maxRemarkLength: number;
}

interface EnvironmentTypeSettings {
	maxFiles: number;
	minStatusWeeks: number;
	minRecoveredOnYears: number;
	maxSinceLength: number;
	maxRemarkLength: number;
}

export interface EnvironmentConfig {
	configuration: 'production' | 'development' | 'test' | 'local' | 'demo';
	baseUrl: string;
	apiVersion: string;
	roleQueryRange: number;
	auth: {
		scopes: string[];
		clientId: string;
		authority: string;
		redirectUri: string;
		knownAuthorities?: string[];
		postLogoutRedirectUri: string;
	};
	showDevTools: boolean;
	lvrEmail: string;
	titlePrefix: string;
	visualDateFormat: string;
	emailRegexStr: string;
	nameRegexStr: string;
	titleRegexStr: string;
	maxTitleLength: number;
	maxNameLength: number;
	branchNameRegexStr: string,
	birthdateLimitYears: number;
	gracePeriodYears: number;
	incapacityForWorkLimitYears: number;
	textInputMaxLength: number;
	textareaMaxLength: number;
	pageSizeOptions: number[];
	defaultPageSize: number;
	emptyDate: string;
	pagination: PaginationConfig;
	contactSettings: EnvironmentContactSettings;
	proofSettings: EnvironmentProofSettings;
	typeSettings: EnvironmentTypeSettings;
	draftStorageConfig: PersistenceConfig;
	formConfiguration: EnvironementFormConfiguration;
	appInsightsConnectionString: string;
	sessionCorrelationKey: string;
	disableAppInsights: boolean;
	fileSettings: EnvironmentFileConfig;
	deployment?: string;
	maxDisabilityProofsPerClient: number;
	maxDisabilityTypesPerClient: number;
	useMockPermissions: boolean;
	useHeaderSearch: boolean;
	wikiStatusBlobUrl: string; 
}

export const defaultFileConfig: EnvironmentFileConfig = {
	maxFileSize: 4 * 1024 * 1024,
	allowedMimeTypes: [
		'application/pdf',
		'image/*',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	],
};

export const defaultContactSettings: EnvironmentContactSettings = {
	maxFiles: 10,
};

export const defaultProofSettings: EnvironmentProofSettings = {
	maxFiles: 10,
	maxExpiryYears: 50,
	maxRemarkLength: 2000,
};

export const defaultTypeSettings: EnvironmentTypeSettings = {
	maxFiles: 10,
	minStatusWeeks: 6,
	minRecoveredOnYears: 2,
	maxSinceLength: 100,
	maxRemarkLength: 300
};

export const ENVIRONMENT = new InjectionToken<EnvironmentConfig>('ENVIRONMENT');


export const buildEnvironment = (base: EnvironmentConfig, overrides: Partial<EnvironmentConfig>): EnvironmentConfig => {
	return { ...base, ...overrides };
};


export const baseEnvironment: EnvironmentConfig = {
	configuration: 'production',
	baseUrl: 'https://dev.klifdweb.de/',
	apiVersion: 'api/v1/',
	roleQueryRange: 12,
	auth: {
		scopes: ['api://b69b4a25-9ffa-4c67-822d-f34648828569/Sample.Req'],
		clientId: 'b69b4a25-9ffa-4c67-822d-f34648828569',
		authority: 'https://devklifdweb.ciamlogin.com/918b14b4-fd4f-4497-a656-1676acb4fdbd/v2.0',
		knownAuthorities: ['devklifdweb.ciamlogin.com'],
		redirectUri: '/',
		postLogoutRedirectUri: '/',
	},
	showDevTools: false,
	lvrEmail: "klifd@lvr.de",
	titlePrefix: 'KlifdWeb - ',
	visualDateFormat: 'TT.MM.JJJJ',
	emailRegexStr: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$', // Requires an '@' and a domain ending in at least 2 characters, e. g. '.de', '.com', etc.
	nameRegexStr: '^\\s*[A-Za-zÀ-ÖØ-öø-ÿ]+(?:[ -][A-Za-zÀ-ÖØ-öø-ÿ]+)*\\s*$', // Allows optional leading/trailing spaces, Latin letters (including accented) and can contain a whitespace and a hyphen between letters
	titleRegexStr: '^\\s*(?:[A-Za-zÀ-ÖØ-öø-ÿ]+(?:\\.[A-Za-zÀ-ÖØ-öø-ÿ]+)*\\.?)(?:[ -][A-Za-zÀ-ÖØ-öø-ÿ]+(?:\\.[A-Za-zÀ-ÖØ-öø-ÿ]+)*\\.?)*\\s*$', // Same as above, but also allows dotted segments within a word (e.g., "M.Sc.", "Dr.-Ing.")
	branchNameRegexStr: '^.*\\S.*$', // Allows any characters, including special characters used in business names, but requires at least one non-whitespace character (e.g., "C&A", "Müller & Söhne GmbH", "Filiale #12 (Nord)")
	birthdateLimitYears: 100,
	gracePeriodYears: 2,
	maxTitleLength: 50,
	maxNameLength: 50,
	incapacityForWorkLimitYears: 10,
	textInputMaxLength: 140,
	textareaMaxLength: 16000, // as in current Klifdweb
	pageSizeOptions: [10, 25, 50],
	defaultPageSize: 10,
	emptyDate: '0001-01-01T00:00:00Z', // Date assigned in BE for empty date values
	pagination: {
		offset: 0,
		range: 500,
	},
	contactSettings: defaultContactSettings,
	proofSettings: defaultProofSettings,
	typeSettings: defaultTypeSettings,
	draftStorageConfig: {
		ttl: 3600, // 1 hour in seconds
		cleanupInterval: 60000, // 1 minute in milliseconds
		staleThreshold: 1800000, // 30 minutes in milliseconds
		storageQuota: 5242880, // 5MB in bytes,
		showToastOnLoad: true,
	},
	formConfiguration: {
		client: {
			birthdateWarningRange: {
				min: 13,
				max: 67,
			},
		},
		contact: {
			dateWarningRange: {
				min: 0,
				max: 42, // days retroactively
			},
			updateCreatePeriodAsDays: 42
		},
		career: {
			startOfCareerRange: {
				min: 60, // 60 years in the past
				max: 2, // 2 years in the future
			},
			typeAndScopeOfTheWorkplaceLength: 300,
			remarkLength: 300,
			jobTitleLength: 300
		},
		assignment: {
			minDateOfCommissionOffsetDays: 42
		},
		lek: {
			resultLength: 300,
		}
	},
	appInsightsConnectionString:
		'InstrumentationKey=0166907b-4020-484f-bb56-c6760ce77034;IngestionEndpoint=https://germanywestcentral-1.in.applicationinsights.azure.com/;LiveEndpoint=https://germanywestcentral.livediagnostics.monitor.azure.com/;ApplicationId=2d4dec14-7636-4933-8a49-eaced46349c9',
	disableAppInsights: false,
	sessionCorrelationKey: 'lvr-session-correlation-id',
	fileSettings: defaultFileConfig,
	deployment: '',
	maxDisabilityProofsPerClient: 10,
	maxDisabilityTypesPerClient: 10,
	useMockPermissions: false,
	useHeaderSearch: true,
	wikiStatusBlobUrl: 'https://stwikigwcklifdwebshared.blob.core.windows.net/wikiexports/wiki-export/wiki-export.json',
};
